/**
 * my_account.repository.ts — 내 계정·환경(D-1) 읽기·쓰기.
 *
 * 설계: docs/design/12_통합관리_계정기기설계.md §4-2 users·members · §4-2 preferences/profile · §8-D.
 *  - users/{uid}            본인 읽기만(쓰기는 admin·함수). 휴대폰·생년월일·근무 법인 목록 표시용
 *  - tenants/{t}/members/{uid} 는 members.repository(watchOne) 한 곳에서 읽는다(2026-10-23 통합)
 *  - users/{uid}/preferences/profile  { photo: data URL ≤ 48,000자, updated_at } 본인 쓰기
 *
 * users.repository.ts(관리자 목록)는 C 묶음 담당이라 본인 화면 전용으로 따로 둔다.
 */
import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "./firebase";
import { isTenantKey, type BizKey, type TenantKey } from "@/domain/space";
import { PROFILE_PHOTO_MAX_CHARS, type MyAccountDoc } from "@/domain/user";
import type { NotifyPolicy } from "@/domain/session";
import { notifyPolicyRepo } from "./notify_policy.repository";

export function watchMyAccountDoc(
  uid: string,
  cb: (d: MyAccountDoc | null) => void,
  onError?: (e: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, "users", uid),
    (snap) => {
      if (!snap.exists()) return cb(null);
      const d = snap.data();
      cb({
        loginId: typeof d.login_id === "string" ? d.login_id : "",
        name: typeof d.name === "string" ? d.name : "",
        phone: typeof d.phone === "string" ? d.phone : "",
        email: typeof d.personal_email === "string" ? d.personal_email : "",
        rank: typeof d.rank === "string" ? d.rank : "",
        birthDate: typeof d.birth_date === "string" ? d.birth_date : "",
        tenants: Array.isArray(d.tenants) ? (d.tenants as unknown[]).filter(isTenantKey) : [],
      });
    },
    (e) => onError?.(e),
  );
}

/** 내 프로필 사진 구독. 없으면 null */
export function watchMyProfilePhoto(
  uid: string,
  cb: (photo: string | null) => void,
): () => void {
  return onSnapshot(
    doc(db, "users", uid, "preferences", "profile"),
    (snap) => {
      const p = snap.exists() ? snap.data().photo : null;
      cb(typeof p === "string" && p.startsWith("data:image/") ? p : null);
    },
    () => cb(null),
  );
}

export async function saveMyProfilePhoto(uid: string, dataUrl: string): Promise<void> {
  if (!dataUrl.startsWith("data:image/")) throw new Error("이미지 형식이 아닙니다.");
  if (dataUrl.length > PROFILE_PHOTO_MAX_CHARS) throw new Error("이미지가 너무 큽니다.");
  await setDoc(
    doc(db, "users", uid, "preferences", "profile"),
    { photo: dataUrl, updated_at: serverTimestamp() },
    { merge: true },
  );
}

/** 사진 지우기 — rules 가 photo 를 문자열로만 받아(§5-2) 필드 삭제 대신 빈 문자열로 둔다 */
export async function removeMyProfilePhoto(uid: string): Promise<void> {
  await setDoc(
    doc(db, "users", uid, "preferences", "profile"),
    { photo: "", updated_at: serverTimestamp() },
    { merge: true },
  );
}

/**
 * 사업부 조직 알림 정책 읽기(D-2 "사업부에서 꺼 둠" 표시) — tenants/{t}/units/{biz}/settings/notify_policy.
 * 편집(법인 환경설정 [알림] 섹션)은 D 묶음 담당. 문서가 없거나 읽을 수 없으면 null(= 전부 켜짐으로 본다).
 */
export function watchUnitNotifyPolicy(
  tenant: TenantKey,
  biz: BizKey,
  cb: (p: NotifyPolicy | null) => void,
): () => void {
  // 읽기·해석은 notify_policy.repository 한 곳(경로 settingsDocPath·런타임 화이트리스트 공유)
  return notifyPolicyRepo.watch({ tenant, biz }, cb, () => cb(null));
}
